import '@data/Simulations/pipeline/blocks/UnobstructedBlock';
import { UnobstructedBlockParameters } from '@data/Simulations/pipeline/blocks/UnobstructedBlock';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { getSentence } from '@gui/constants/sentences';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { Box, Checkbox, FormControl, FormControlLabel, Radio, RadioGroup, useTheme } from '@mui/material';
import { UMBetaBadge } from '@urbanmetrix/ui-kit';
import { BlockParameters, UnobstructedMode } from '@visualizer/types';
import React, { useEffect, useMemo } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type UnobstructedParametersProps = {
    blockId: string;
};

export const getUnobstructedDefaultParams = (): Pick<UnobstructedBlockParameters, 'unobstructedMode' | 'isValue'> => ({
    unobstructedMode: UnobstructedMode.Corners,
    isValue: false,
});

const UnobstructedParameters: React.FC<UnobstructedParametersProps> = ({ blockId }) => {
    const theme = useTheme();
    // Zustand Variables:
    const params = usePipelineBlockParams<UnobstructedBlockParameters>(blockId);

    // Zustand Actions:
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const updateParams = (partial: Partial<UnobstructedBlockParameters>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    // Initialize missing params on mount
    useEffect(() => {
        if (!params) {
            updateParams(getUnobstructedDefaultParams());
            return;
        }

        const missing: Partial<UnobstructedBlockParameters> = {};

        if (params.unobstructedMode == null) missing.unobstructedMode = UnobstructedMode.Corners;
        if (params.isValue == null) missing.isValue = false;

        if (Object.keys(missing).length > 0) updateParams(missing);
    }, [params]);

    if (!params) return null;

    const unobstructedMode = params.unobstructedMode ?? UnobstructedMode.Corners;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl className="panel-compact-control-stack">
                <Box>
                    <RadioGroup row value={unobstructedMode} onChange={(e) => updateParams({ unobstructedMode: Number(e.target.value) })}>
                        <FormControlLabel
                            value={UnobstructedMode.Center}
                            control={<Radio size="small" />}
                            label={
                                <CustomTooltip side="right" title={getSentence('Pipeline.Filters.Unobstructed.Center')}>
                                    <span>Center</span>
                                </CustomTooltip>
                            }
                        />
                        <FormControlLabel
                            value={UnobstructedMode.Corners}
                            control={<Radio size="small" />}
                            label={
                                <CustomTooltip side="right" title={getSentence('Pipeline.Filters.Unobstructed.Corners')}>
                                    <span>Corners</span>
                                </CustomTooltip>
                            }
                        />
                    </RadioGroup>
                </Box>
            </FormControl>

            <FormControl sx={{ mr: 2, display: 'inline-flex', alignItems: 'flex-start' }} className="panel-compact-control-stack">
                <FormControlLabel
                    sx={{
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': {
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.25,
                        },
                        '& .MuiCheckbox-root': {
                            mt: '1px',
                        },
                    }}
                    control={
                        <Checkbox
                            size="small"
                            checked={params.isValue ?? false}
                            onChange={(e) => updateParams({ isValue: e.target.checked })}
                        />
                    }
                    label={
                        <CustomTooltip side="right" title={getSentence('Pipeline.Filters.Unobstructed.UpdateValues')}>
                            <Box
                                component="span"
                                sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}
                            >
                                <span>Update Values</span>
                                <UMBetaBadge theme={theme.palette.mode === 'dark' ? 'dark' : 'light'} />
                            </Box>
                        </CustomTooltip>
                    }
                />
            </FormControl>
        </Box>
    );
};

export default UnobstructedParameters;
